/**
 * Search names for the tag-icon picker, in all 13 locales.
 *
 * `keywords/<locale>.json` maps every synced tag-icon key to 2–8 names in that
 * language; the first is the icon's display name (tooltip + accessibility
 * label), the rest are words people type to find it. The picker searches all
 * locales at once (`searchIcons` in @caliu/utils), so a query in any language
 * finds the icon whatever the app language is. Swift bundles the same names
 * (apps/swift/scripts/generate-icons.py → TagIconKeywords.json).
 */

import ca from "./keywords/ca.json";
import de from "./keywords/de.json";
import en from "./keywords/en.json";
import es from "./keywords/es.json";
import fr from "./keywords/fr.json";
import it from "./keywords/it.json";
import ja from "./keywords/ja.json";
import ko from "./keywords/ko.json";
import nl from "./keywords/nl.json";
import pt from "./keywords/pt.json";
import ru from "./keywords/ru.json";
import zhCN from "./keywords/zh-CN.json";
import zhTW from "./keywords/zh-TW.json";
import { TAG_CATEGORIES, tagIconsByCategory } from "./index";

type Names = Record<string, string[]>;

export const TAG_ICON_KEYWORDS_BY_LOCALE: Record<string, Names> = {
  en, es, ca, fr, de, it, pt, nl, ja, "zh-CN": zhCN, ko, "zh-TW": zhTW, ru,
};

/** `tagIconKeywords[tagKey][locale]` = names, keys in picker display order. */
export const tagIconKeywords: Record<string, Record<string, string[]>> = Object.fromEntries(
  TAG_CATEGORIES.flatMap((category) => tagIconsByCategory[category.id]).map((icon) => {
    const key = icon.tag as string;
    const names = Object.fromEntries(
      Object.entries(TAG_ICON_KEYWORDS_BY_LOCALE).map(([locale, byKey]) => [locale, byKey[key] ?? []]),
    );
    return [key, names];
  }),
);

/** The display name of a tag icon in `locale`, falling back to English. */
export function tagIconDisplayName(tagKey: string, locale: string): string | undefined {
  const names = tagIconKeywords[tagKey];
  if (!names) return undefined;
  return names[locale]?.[0] ?? names.en?.[0];
}
